import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { type LanguageLevel, type Lesson } from '../types';
import { supabase } from '../utils/supabase';
import { Lock, CheckCircle, PlusCircle } from 'lucide-react';
import { getLessonsTable, getProgressTable } from '../utils/supabase';
import { CustomLessonRequestModal } from './CustomLessonRequestModal';

interface LessonListProps {
  level: string;
}

interface LevelStats {
  total: number;
  completed: number;
}


interface LessonWithProgress extends Lesson {
  completed?: boolean;
  locked?: boolean;
}
export function LessonList({ level }: LessonListProps) {
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState<LessonWithProgress[]>([]);
  const [progress, setProgress] = useState(0);
  const [levelStats, setLevelStats] = useState<LevelStats>({ total: 0, completed: 0 });
  const [showCustomLessonModal, setShowCustomLessonModal] = useState(false);
  const navigate = useNavigate();
  const mountedRef = useRef(false);
  const [currentLevel, setCurrentLevel] = useState<string>(() => {
    // Initialize from prop or localStorage, with prop taking precedence
    return level || localStorage.getItem('currentLevel') || 'A1';
  });

  // Update current level when prop changes
  useEffect(() => {
    if (level) {
      console.log('Level prop changed to:', level);
      setCurrentLevel(level);
      // Also update localStorage to ensure persistence
      localStorage.setItem('currentLevel', level);
    }
  }, [level]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const loadLessons = async () => {
      console.log('Loading lessons for level:', currentLevel);

      // Always store current level in localStorage for persistence
      if (currentLevel) {
        localStorage.setItem('currentLevel', currentLevel);
      }

      try {
        setLoading(true);

        const { data: { user } } = await supabase.auth.getUser(); 
        if (!user) throw new Error('No user found');

        const [
          { data: lessons, error: lessonError }, 
          { data: progress, error: progressError },
          { data: allLessons, error: allLessonsError }
        ] = await Promise.all([
          supabase
            .from(getLessonsTable())
            .select(`
              id,
              level,
              title,
              description,
              category,
              difficulty,
              is_review,
              order_index,
              created_at
            `)
            .eq('is_review', false)
            .eq('level', currentLevel)
            .order('order_index', { ascending: true })
            .order('created_at', { ascending: true }),
          supabase
            .from(getProgressTable())
            .select('*')
            .eq('user_id', user.id),
          supabase
            .from(getLessonsTable())
            .select('level')
            .eq('is_review', false)
        ]);

        // Check if component is still mounted before updating state
        if (!mountedRef.current) return;

        if (lessonError) throw new Error(lessonError.message);
        if (progressError) throw progressError;
        if (allLessonsError) throw allLessonsError;

        // Calculate total lessons for each level
        const levelCounts = (allLessons || []).reduce((acc, lesson) => {
          acc[lesson.level] = (acc[lesson.level] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        const completedCount = (progress || [])
          .filter(p => p.completed)
          .length;

        setLevelStats({
          total: levelCounts[currentLevel] || 0,
          completed: completedCount
        });

        // Sort lessons by order_index and creation date
        const sortedLessons = (lessons || []).sort((a, b) => {
          if (a.order_index !== b.order_index) {
            return (a.order_index || 0) - (b.order_index || 0);
          }
          return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
        });

        // Create progress map for quick lookups
        const progressMap = new Map(
          progress?.map(p => [p.lesson_id, p.completed]) || []
        );
        
        // Check if user is admin using email check instead of profiles query
        const isAdmin = ['alinickmaleki@gmail.com', 'alinickmaleki@ymail.com'].includes(user.email || '');
        
        // Process lessons - all unlocked for admin, normal rules for others
        const processedLessons = sortedLessons.map((lesson, index) => {
          const isCompleted = progressMap.get(lesson.id) || false;
          let isLocked = !isAdmin && index > 0 && !progressMap.get(sortedLessons[index - 1].id);

          return {
            ...lesson,
            completed: isCompleted,
            locked: isLocked
          }
        });

        setLessons(processedLessons);

        // Calculate progress percentage
        const completedLessonsCount = processedLessons.filter(lesson => lesson.completed).length;
        const totalLessons = processedLessons.length;
        setProgress(totalLessons > 0 ? (completedLessonsCount / totalLessons) * 100 : 0);

      } catch (error) {
        console.error('Error loading lessons:', error);
        if (mountedRef.current) {
          setLessons([]);
          setProgress(0);
        }
        // TODO: Add error state and display to user
        if (error instanceof Error) {
          if (error.message.includes('relation "persian_lesson_content" does not exist')) {
            console.warn('Persian lessons table not found, this is expected for first-time setup');
          } else {
            console.error('Error details:', error.message);
          }
          return;
        }
        // TODO: Add error state and display to user
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadLessons();
  }, [currentLevel]);

  const handleLessonClick = async (lesson: LessonWithProgress) => {
    if (lesson.locked) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Create or update progress entry for this lesson
      const { error } = await supabase
        .from(getProgressTable())
        .upsert({
          user_id: user.id,
          lesson_id: lesson.id,
          completed: false
        }, {
          onConflict: 'user_id,lesson_id'
        });
      
      if (error) {
        console.error('Error creating/updating lesson progress:', error);
      }

      navigate(`/lesson/${lesson.id}`);
    } catch (error) {
      console.error('Error creating lesson progress:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto"></div>
            <div className="space-y-3">
              <div className="h-20 bg-gray-200 rounded"></div>
              <div className="h-20 bg-gray-200 rounded"></div>
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!lessons.length) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-center text-3xl font-extrabold text-gray-900 mb-8">
            No Lessons Available
          </h2>
          <p className="text-center text-gray-600">
            There are currently no lessons available for this level.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-center text-3xl font-extrabold text-gray-900 mb-4">
          Level {currentLevel} Lessons
          <div className="text-lg font-normal text-gray-600 mt-2">
            {levelStats.total} {levelStats.total === 1 ? 'Lesson' : 'Lessons'} Available
          </div>
          <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {Math.round(progress)}% Complete
          </p>
        </h2>
        <div className="space-y-4">
          {lessons.map((lesson) => (
            <div
              key={lesson.id}
              className={`bg-white p-4 rounded-lg shadow-md transition-shadow duration-200 relative ${
                lesson.locked ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-lg cursor-pointer'
              }`}
              onClick={() => handleLessonClick(lesson)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 pr-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    <div className="flex items-center gap-2">
                      <span>{lesson.title}</span>
                    {lesson.locked ? (
                      <Lock className="h-4 w-4 text-gray-400" />
                    ) : lesson.completed ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : null}
                    </div>
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {lesson.description || 'Click to start the lesson'}
                  </p>
                  <div className="mt-2 flex items-center space-x-4">
                    <span className="text-sm text-gray-500">
                      {lesson.category || 'General'}
                    </span>
                    <span className={`text-sm px-2 py-1 rounded ${
                      lesson.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                      lesson.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {lesson.difficulty || 'medium'}
                    </span>
                  </div>
                </div>
                {lesson.completed && !lesson.locked && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/lesson/${lesson.id}/practice`, {
                        state: { skipToFinalText: true }
                      });
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 px-6 py-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg"
                    style={{display: 'block'}}
                  >
                    Read
                  </button>
                )}
              </div>
            </div>
          ))}

          <div
            className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow-md border-2 border-dashed border-blue-300 hover:border-blue-500 cursor-pointer transition-all duration-200 hover:shadow-lg"
            onClick={() => setShowCustomLessonModal(true)}
          >
            <div className="flex items-center gap-4">
              <div className="bg-blue-500 p-3 rounded-full">
                <PlusCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Request Custom Lesson
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Have a Dutch text you need to study? Submit it here and we'll create a lesson for you!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCustomLessonModal && (
        <CustomLessonRequestModal
          level={currentLevel}
          onClose={() => setShowCustomLessonModal(false)}
          onSuccess={() => {
            setShowCustomLessonModal(false);
            alert('Your custom lesson request has been submitted! We will notify you when it\'s ready.');
          }}
        />
      )}
    </div>
  );
}